import { useCallback, useRef, useState } from "react";
import { useBasisTheory } from "@basis-theory/react-elements";
import { authorizeVaultSession } from "../api/api";

export const VAULT_NOT_ENABLED = "vault_not_enabled";

/** True when the backend says Altruon vaulting is off for this checkout. */
export const isVaultNotEnabledError = (error) =>
  error?.response?.status === 409 ||
  error?.response?.data?.error === VAULT_NOT_ENABLED;

/**
 * Basis Theory card capture for the hosted checkout. Must be used under BasisTheoryProvider.
 *
 * Card data stays inside Basis Theory's iframes. The page only ever holds a token intent id:
 * the backend authorizes the Elements session for this checkout, then the intent is created
 * from the card element and handed to the Altruon backend, which charges through the proxy.
 */
export const useBasisTheoryCard = ({ sessionId }) => {
  const { bt, error: sdkError } = useBasisTheory();
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  const [cardBrand, setCardBrand] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  const onReady = useCallback(() => setReady(true), []);

  const onChange = useCallback((event) => {
    const detail = event?.detail || {};
    setComplete(Boolean(detail.isValid && !detail.isEmpty));
    setCardBrand(detail.cardBrand || null);
    setFieldError(detail.isEmpty ? null : detail.error?.message || null);
  }, []);

  const createTokenIntent = useCallback(async () => {
    const card = cardRef.current;
    if (!bt || !card) {
      throw new Error("Card form not ready");
    }
    const session = await bt.sessions.create();
    await authorizeVaultSession(sessionId, session?.nonce);
    const intent = await bt.tokenIntents.create({
      type: "card",
      data: {
        number: card.number,
        expiration_month: card.expiryDate,
        expiration_year: card.expiryDate,
        cvc: card.cvc,
      },
    });
    if (!intent?.id) {
      throw new Error("Card could not be secured");
    }
    return {
      tokenIntentId: intent.id,
      brand: intent?.card?.brand || card.cardBrand || cardBrand || null,
    };
  }, [bt, sessionId, cardBrand]);

  return {
    bt,
    sdkError,
    cardRef,
    ready,
    complete,
    cardBrand,
    fieldError,
    onReady,
    onChange,
    createTokenIntent,
  };
};
